/**
 * Referral Store
 *
 * Zustand store for managing referral codes, tracking, and rewards.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { getStorage } from '@/lib/state/ssr-storage';

import {
  ReferralCode,
  Referral,
  ReferralReward,
  ReferralStats,
  ReferralStatus,
  generateReferralCode,
  isWithinConversionWindow,
  isRewardValid,
  REFERRAL_DISCOUNT_PERCENT,
  REFERRAL_CONVERSION_WINDOW_DAYS,
  REFERRAL_REWARD_EXPIRY_DAYS,
} from './types';

// ============================================================================
// State Interface
// ============================================================================

interface ReferralState {
  // User's referral code
  myReferralCode: ReferralCode | null;

  // Referrals I've made (people I referred)
  myReferrals: Referral[];

  // Referrals I'm part of (who referred me)
  referredBy: Referral | null;

  // My rewards
  rewards: ReferralReward[];

  // Applied referral code (at signup)
  appliedCode: string | null;

  // Pending referral code (from URL, not yet applied)
  pendingCode: string | null;

  // Loading states
  isLoading: boolean;

  // Actions
  generateMyCode: (userId: string, userType: 'startup' | 'investor') => ReferralCode;
  setPendingCode: (code: string | null) => void;
  applyPendingCode: (refereeId: string, refereeType: 'startup' | 'investor') => Promise<{ success: boolean; error?: string }>;
  applyReferralCode: (code: string, refereeId: string, refereeType: 'startup' | 'investor') => Promise<{ success: boolean; error?: string }>;
  trackReferralSignup: (refereeId: string, refereeType: 'startup' | 'investor') => void;
  markReferralConverted: (referralId: string) => void;
  applyReward: (rewardId: string) => void;
  getStats: () => ReferralStats;
  getActiveReward: () => ReferralReward | null;
  hasActiveDiscount: () => boolean;
  clearPendingCode: () => void;
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  myReferralCode: null,
  myReferrals: [],
  referredBy: null,
  rewards: [],
  appliedCode: null,
  pendingCode: null,
  isLoading: false,
};

// ============================================================================
// Store
// ============================================================================

export const useReferralStore = create<ReferralState>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Generate a unique referral code for the user
       */
      generateMyCode: (userId, userType) => {
        const existing = get().myReferralCode;
        if (existing && existing.userId === userId && existing.isActive) {
          return existing;
        }

        const code: ReferralCode = {
          code: generateReferralCode(userType),
          userId,
          userType,
          createdAt: new Date().toISOString(),
          currentUses: 0,
          isActive: true,
        };

        set({ myReferralCode: code });
        return code;
      },

      /**
       * Set a pending referral code (from URL)
       */
      setPendingCode: (code) => {
        if (code && !get().appliedCode) {
          set({ pendingCode: code.toUpperCase() });
        }
      },

      /**
       * Clear pending code
       */
      clearPendingCode: () => {
        set({ pendingCode: null });
      },

      /**
       * Apply the pending referral code
       */
      applyPendingCode: async (refereeId, refereeType) => {
        const { pendingCode } = get();
        if (!pendingCode) {
          return { success: false, error: 'No pending code' };
        }

        const result = await get().applyReferralCode(pendingCode, refereeId, refereeType);

        if (result.success) {
          set({ pendingCode: null });
        }

        return result;
      },

      /**
       * Apply a referral code at signup
       */
      applyReferralCode: async (code, refereeId, refereeType) => {
        set({ isLoading: true });

        // Simulate API validation delay
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Validate code format
        const codeRegex = /^(INV|FND)-[A-Z0-9]{6}$/;
        if (!codeRegex.test(code.toUpperCase())) {
          set({ isLoading: false });
          return { success: false, error: 'Invalid referral code format' };
        }

        // In a real app, this would validate against the backend
        // For now, accept any valid-formatted code

        const referral: Referral = {
          id: `ref_${Date.now()}`,
          referrerId: 'unknown', // Would come from backend
          referrerType: code.startsWith('INV') ? 'startup' : 'investor',
          refereeId,
          refereeType,
          code: code.toUpperCase(),
          status: 'pending',
          createdAt: new Date().toISOString(),
        };

        set({
          referredBy: referral,
          appliedCode: code.toUpperCase(),
          isLoading: false,
        });

        return { success: true };
      },

      /**
       * Track when a referred user signs up
       */
      trackReferralSignup: (refereeId, refereeType) => {
        const { myReferralCode, myReferrals } = get();
        if (!myReferralCode) return;

        const referral: Referral = {
          id: `ref_${Date.now()}`,
          referrerId: myReferralCode.userId,
          referrerType: myReferralCode.userType,
          refereeId,
          refereeType,
          code: myReferralCode.code,
          status: 'pending',
          createdAt: new Date().toISOString(),
        };

        set({
          myReferrals: [...myReferrals, referral],
          myReferralCode: {
            ...myReferralCode,
            currentUses: myReferralCode.currentUses + 1,
          },
        });
      },

      /**
       * Mark a referral as converted (referee subscribed)
       */
      markReferralConverted: (referralId) => {
        const { myReferrals, rewards } = get();

        const updatedReferrals = myReferrals.map((r) => {
          if (r.id !== referralId) return r;

          // Check if still within conversion window
          if (!isWithinConversionWindow(r.createdAt)) {
            return { ...r, status: 'expired' as ReferralStatus };
          }

          return {
            ...r,
            status: 'converted' as ReferralStatus,
            convertedAt: new Date().toISOString(),
          };
        });

        // Create reward for the referrer
        const convertedReferral = updatedReferrals.find((r) => r.id === referralId);
        if (convertedReferral && convertedReferral.status === 'converted') {
          const expiresAt = new Date();
          expiresAt.setDate(expiresAt.getDate() + REFERRAL_REWARD_EXPIRY_DAYS);

          const reward: ReferralReward = {
            id: `reward_${Date.now()}`,
            referralId,
            referrerId: convertedReferral.referrerId,
            type: 'discount_percent',
            value: REFERRAL_DISCOUNT_PERCENT,
            expiresAt: expiresAt.toISOString(),
            status: 'active',
          };

          set({
            myReferrals: updatedReferrals,
            rewards: [...rewards, reward],
          });
        } else {
          set({ myReferrals: updatedReferrals });
        }
      },

      /**
       * Apply (use) a reward
       */
      applyReward: (rewardId) => {
        const { rewards } = get();

        set({
          rewards: rewards.map((r) =>
            r.id === rewardId
              ? { ...r, status: 'used' as const, usedAt: new Date().toISOString() }
              : r
          ),
        });
      },

      /**
       * Get referral statistics
       */
      getStats: () => {
        const { myReferrals, rewards } = get();

        const pending = myReferrals.filter((r) => r.status === 'pending');
        const converted = myReferrals.filter((r) => r.status === 'converted' || r.status === 'rewarded');

        const activeRewards = rewards.filter((r) => r.status === 'active' && isRewardValid(r));
        const usedRewards = rewards.filter((r) => r.status === 'used');

        // Calculate total savings (simplified: assume monthly plan at CHF 49)
        const basePriceForCalc = 4900; // CHF 49 in cents
        const totalEarned = converted.length * Math.round(basePriceForCalc * REFERRAL_DISCOUNT_PERCENT / 100);

        return {
          totalReferrals: myReferrals.length,
          pendingReferrals: pending.length,
          convertedReferrals: converted.length,
          totalRewardsEarned: totalEarned,
          rewardsUsed: usedRewards.length,
          rewardsPending: activeRewards.length,
        };
      },

      /**
       * Get the active (unused, not expired) reward
       */
      getActiveReward: () => {
        const { rewards } = get();
        return rewards.find((r) => r.status === 'active' && isRewardValid(r)) || null;
      },

      /**
       * Check if user has an active discount to apply
       */
      hasActiveDiscount: () => {
        return get().getActiveReward() !== null;
      },

      /**
       * Reset referral state
       */
      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'inverio-referrals',
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        myReferralCode: state.myReferralCode,
        myReferrals: state.myReferrals,
        referredBy: state.referredBy,
        rewards: state.rewards,
        appliedCode: state.appliedCode,
        pendingCode: state.pendingCode,
      }),
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const useMyReferralCode = () => useReferralStore((s) => s.myReferralCode);

export const useReferralStats = () => {
  const { myReferrals, rewards } = useReferralStore(
    useShallow((s) => ({ myReferrals: s.myReferrals, rewards: s.rewards }))
  );

  const pending = myReferrals.filter((r) => r.status === 'pending');
  const converted = myReferrals.filter((r) => r.status === 'converted' || r.status === 'rewarded');
  const activeRewards = rewards.filter((r) => r.status === 'active' && isRewardValid(r));
  const usedRewards = rewards.filter((r) => r.status === 'used');

  const basePriceForCalc = 4900; // CHF 49 in cents
  const totalEarned = converted.length * Math.round(basePriceForCalc * REFERRAL_DISCOUNT_PERCENT / 100);

  return {
    totalReferrals: myReferrals.length,
    pendingReferrals: pending.length,
    convertedReferrals: converted.length,
    totalRewardsEarned: totalEarned,
    rewardsUsed: usedRewards.length,
    rewardsPending: activeRewards.length,
  } as ReferralStats;
};

export const useHasActiveDiscount = () => {
  const rewards = useReferralStore((s) => s.rewards);
  return rewards.some((r) => r.status === 'active' && isRewardValid(r));
};
